import React, { useEffect, useState } from "react"
import clone from "lodash/clone"
import find from "lodash/find"
import filter from "lodash/filter"

import AuthenticationTestEnsureTokenDoesNotExpire from "partners/components/authentication_test/token_not_expire"
import AuthenticationTestRefreshAccessToken from "partners/components/authentication_test/refresh_token"
import AuthenticationTestRetrieveAuthCode from "partners/components/authentication_test/retrieve_auth_code"
import AuthenticationTestRetrieveUserInfo from "partners/components/authentication_test/retrieve_user_info"
import AuthenticationTestRequestToken from "partners/components/authentication_test/request_token"

interface Props {
  activation: {
    code: string
  }
  activation_path: string
  refresh_token_path: string
  request_token_path: string
  retrieve_user_info_path: string
  service: {
    oauth_use_refresh_token: boolean
    authentication_type: string
  }
}

const AuthenticationTest = ({
  activation,
  activation_path,
  refresh_token_path,
  request_token_path,
  retrieve_user_info_path,
  service,
}: Props) => {
  const initialStatuses: { [key: string]: string } = { retrieveUserInfo: "" }
  if (service.authentication_type === "oauth") {
    initialStatuses.retrieveAuthCode = ""
    initialStatuses.requestToken = ""
  }
  if (service.authentication_type === "oauth_refresh_token") {
    initialStatuses.refreshAccessToken = ""
    initialStatuses.tokenNotExpire = ""
  }
  const [statuses, setStatuses] = useState(initialStatuses)
  const [combinedStatus, setCombinedStatus] = useState("")
  const isShowing = !!activation
  const auth = activation

  useEffect(() => {
    const testStatusListener = window.addEventListener("statusReceived", event => {
      const statusesClone = clone(statuses)
      let newCombinedStatus = ""

      statusesClone[event.detail.componentKey] = event.detail.componentStatus

      if (find(statusesClone, value => value === "failing")) {
        newCombinedStatus = "failing"
      } else if (filter(statusesClone, value => value === "passing").length === Object.keys(statusesClone).length) {
        newCombinedStatus = "passing"
      }
      setStatuses(statusesClone)
      setCombinedStatus(newCombinedStatus)
    })

    return () => {
      window.removeEventListener("statusReceived", testStatusListener)
    }
  }, [])

  let titleText = "Authentication tests pending."
  if (combinedStatus === "failing") {
    titleText = "Authentication tests failed."
  } else if (combinedStatus === "passing") {
    titleText = "Authentication tests successful."
  }
  const isApiTokenAuth = service.authentication_type === "api_token_auth"
  const isOauth = ["oauth", "oauth_pkce"].includes(service.authentication_type)
  const isRefreshTokenAuth = ["oauth_refresh_token", "oauth_pkce_refresh_token"].includes(service.authentication_type)

  return (
    <div className={`publish-item heading ${combinedStatus}`}>
      <header>{titleText}</header>
      {isShowing && (isApiTokenAuth || isOauth || isRefreshTokenAuth) && (
        <>
          {(isOauth || isRefreshTokenAuth) && (
            <>
              <AuthenticationTestRetrieveAuthCode auth={auth} path={activation_path} service={service} />
              <AuthenticationTestRequestToken code={activation.code} path={request_token_path} service={service} />
            </>
          )}
          <AuthenticationTestRetrieveUserInfo
            path={retrieve_user_info_path}
            service={service}
            authentication_type={service.authentication_type}
          />
          {isRefreshTokenAuth && (
            <>
              <AuthenticationTestRefreshAccessToken path={refresh_token_path} service={service} />
              <AuthenticationTestEnsureTokenDoesNotExpire path={refresh_token_path} service={service} />
            </>
          )}
        </>
      )}
    </div>
  )
}

export default AuthenticationTest
